type NavigationMatch = {
	path?: string;
	params: {
		navigationItemId?: string | null;
	};
};

export type ContainerNavigationInput =
	| { scopeId: string }
	| { projectKeyQuery: { cloudId: string; projectKey: string } }
	| { projectKeyAndItemIdQuery: { cloudId: string; projectKey: string; itemId: string } };

type ContainerNavigationInputArgs = {
	cloudId: string;
	/** Board or project ARI, when the frontend already knows the container scope. */
	scopeId: string;
	projectKey: string;
	match: NavigationMatch;
	useItemIdLookup: boolean;
};

/**
 * Navigation item ids are namespaced by item type. The default List view is addressed as `list`
 * and a saved List view as `list/<slug>`.
 */
const LIST_NAVIGATION_ITEM_ID = 'list';

/**
 * Matches the `/list` segment of a List route path, e.g.
 * `/jira/software/c/projects/:projectKey/list/:navigationItemId?`.
 */
const LIST_ROUTE_SEGMENT = /\/list(\/|$)/;

/**
 * Resolves the navigation item id addressed by the current route, or `null` when the route does
 * not address an item the container navigation query can resolve from a project key alone.
 */
const getListNavigationItemId = (match: NavigationMatch): string | null => {
	if (!LIST_ROUTE_SEGMENT.test(match.path ?? '')) {
		return null;
	}

	const itemIdSlug = match.params.navigationItemId;

	return itemIdSlug ? `${LIST_NAVIGATION_ITEM_ID}/${itemIdSlug}` : LIST_NAVIGATION_ITEM_ID;
};

/**
 * Builds the `containerNavigationInput` for the Jira horizontal navigation resource query.
 */
export const getContainerNavigationInput = ({
	cloudId,
	scopeId,
	projectKey,
	match,
	useItemIdLookup,
}: ContainerNavigationInputArgs): ContainerNavigationInput => {
	if (scopeId) {
		return { scopeId };
	}

	const itemId = getListNavigationItemId(match);

	if (projectKey && itemId && useItemIdLookup) {
		return { projectKeyAndItemIdQuery: { cloudId, projectKey, itemId } };
	}

	return { projectKeyQuery: { cloudId, projectKey } };
};
